import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { RiArrowRightWideFill } from 'react-icons/ri';
import { buildFlagGroups, type FeatureFlag, type FlagGroup } from './FeatureFlagConfig.utils';
import { _isEmpty } from '@/littledash';
import { NoDataMessage } from '@/components/NoDataCard';
import { type Control, useWatch } from 'react-hook-form';
import FlagRow from './FlagRow';
import styles from './FeatureFlagConfig.module.scss';

const TH_CLASSES = 'f7 f6-l ph2 pv2 ph3-l pv3-l near-black basier-med normal';

interface FlagSectionProps {
  title: string;
  description: string;
  flags: Array<FeatureFlag>;
  control: Control;
}

const FlagSection: FC<FlagSectionProps> = ({ title, description, flags, control }) => {
  const [open, setOpen] = useState(true);
  const groups = useMemo(() => buildFlagGroups(flags), [flags]);

  const chevronClass = [styles.sectionChevron, open && styles.sectionChevronOpen].filter(Boolean).join(' ');

  return (
    <div data-test-component="FlagSection" data-test-element="container" data-test-key={title}>
      <div
        className="flex items-start pv3 pointer"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`Toggle ${title} section`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        <RiArrowRightWideFill size={18} className={chevronClass} />
        <div>
          <div className="flex items-center">
            <span className="f4 near-black">{title}</span>
            <span className={styles.countBadge}>{flags.length}</span>
          </div>
          <p className="f6 dark-gray mt1 mb0">{description}</p>
        </div>
      </div>
      {open &&
        (_isEmpty(flags) ? (
          <NoDataMessage title="No items configured in this section." />
        ) : (
          <div className="ui__table bg-white w-100 b-moon--gray ba">
            <table className="w-100 tl" style={{ borderSpacing: 0, fontSize: 11 }}>
              <thead className="bg-near-white">
                <tr>
                  <th className={`${TH_CLASSES} br`}>Name</th>
                  <th className={`${TH_CLASSES} br`}>Description</th>
                  <th className={`${TH_CLASSES} br`}>Value</th>
                </tr>
              </thead>
              <tbody className="black">
                {groups.map((group) => (
                  <FlagGroupRows key={group.parent.name} group={group} control={control} />
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
};

interface FlagGroupRowsProps {
  group: FlagGroup;
  control: Control;
}

const FlagGroupRows: FC<FlagGroupRowsProps> = ({ group, control }) => {
  const [expanded, setExpanded] = useState(false);
  const parentEnabled = useWatch({ control, name: group.parent.name, defaultValue: group.parent.value });
  // Children can only be revealed while the parent flag is enabled, so a disabled parent hides both
  // the expand control and any children.
  const canExpand = group.children.length > 0 && Boolean(parentEnabled);

  // Auto-expand children when the parent transitions from disabled to enabled, so the newly-available
  // children are visible immediately. The user can still collapse them afterwards.
  const wasEnabled = useRef(Boolean(parentEnabled));
  useEffect(() => {
    if (Boolean(parentEnabled) && !wasEnabled.current) {
      setExpanded(true);
    }
    wasEnabled.current = Boolean(parentEnabled);
  }, [parentEnabled]);

  return (
    <>
      <FlagRow
        flag={group.parent}
        control={control}
        expandState={canExpand ? { expanded, onToggle: () => setExpanded((e) => !e) } : undefined}
      />
      {canExpand && expanded && <ChildRows childFlags={group.children} control={control} />}
    </>
  );
};

interface ChildRowsProps {
  childFlags: Array<FeatureFlag>;
  control: Control;
}

const ChildRows: FC<ChildRowsProps> = ({ childFlags, control }) => (
  <>
    {childFlags.map((child) => (
      <FlagRow key={child.name} flag={child} control={control} nested />
    ))}
  </>
);

export default FlagSection;
